/**
 * PipelinePerformance — Statistics 10A redesign.
 *
 * Layout: per-mockup `Image #22`.
 *   left strip  — 5 ServiceStripRow (avg, sparkline, delta)
 *   center      — multi-line chart (5 series, phos accent on active)
 *   below chart — RegressionCallout per regression
 *   bottom-left — Top 8 slowest steps table
 *   bottom-right— Service share stacked bar + key list
 *
 * Range filter sits in the page header (handled in Statistics.jsx).
 *
 * Data: /api/stats/performance + /api/stats/slow-steps. Both UNION
 * live disk meta + PhotoArchive so 30-day ranges survive Empty
 * Environment. See matrix/docs/STATISTICS_METRICS.md.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  fetchStatsPerformance,
  fetchStatsSlowSteps,
} from '../../../api';
import {
  LineChart,
  Mono,
  RegressionCallout,
  ServiceStripRow,
  Sparkline,
  StackedBar,
} from '../../../shared/components/redesign';
import { SERVICE_LABELS, SERVICE_DESCRIPTIONS, SERVICE_ORDER } from '../constants';
import { formatMs, formatSeconds } from '../utils';
import styles from './PipelinePerformance.module.css';

// Key used in selectedServices + LineChart for the end-to-end pipeline
// wall-clock series. Matches the series key produced below.
const TOTAL_KEY = 'total';

export default function PipelinePerformance({ range = '30d', includeArchived = false }) {
  const [perf, setPerf] = useState(null);
  const [slowSteps, setSlowSteps] = useState(null);
  const [loading, setLoading] = useState(true);
  // Multi-select pills — default to just 'sati' so the initial chart
  // matches the prior single-active behaviour; users add more by
  // clicking strip rows or legend chips. Empty set = no filter (all
  // series rendered at full opacity).
  const [selectedServices, setSelectedServices] = useState(() => new Set(['sati']));

  const toggleService = (key) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchStatsPerformance(range, includeArchived),
      fetchStatsSlowSteps(range, 8, includeArchived),
    ])
      .then(([p, s]) => {
        if (cancelled) return;
        setPerf(p);
        setSlowSteps(s);
      })
      .catch((e) => {
        // Endpoints may not be deployed yet on legacy instances —
        // fail soft so the UI degrades to "no data" rather than crashing.
        console.warn('PipelinePerformance: fetch failed', e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, includeArchived]);

  // Derive a per-service trend (downsampled from `series.points` to 9
  // for the strip-row sparkline). Also computes the 'total' sparkline
  // from wall_clock_series so the Total pipeline pill gets its own
  // trend visualization.
  const sparkByService = useMemo(() => {
    const out = {};
    const targetLen = 9;
    const downsample = (pts) => {
      const valued = (pts || []).filter((p) => Number.isFinite(p));
      if (valued.length === 0) return [];
      const stride = Math.max(1, Math.floor(valued.length / targetLen));
      const sampled = [];
      for (let i = 0; i < valued.length; i += stride) {
        sampled.push(valued[i]);
        if (sampled.length >= targetLen) break;
      }
      return sampled;
    };
    (perf?.series || []).forEach((s) => {
      out[s.key] = downsample(s.points);
    });
    if (perf?.wall_clock_series?.points?.length) {
      out[TOTAL_KEY] = downsample(perf.wall_clock_series.points);
    }
    return out;
  }, [perf]);

  if (loading) {
    return <div className={styles.loading}>Loading performance metrics…</div>;
  }

  if (!perf) {
    return (
      <div className={styles.empty}>
        <Mono className={styles.eyebrow}>Performance</Mono>
        <p>No data available for the selected range.</p>
      </div>
    );
  }

  const services = perf.services || [];
  const baseSeries = (perf.series || []).map((s) => ({
    ...s,
    label: SERVICE_LABELS[s.key] || s.key,
  }));
  // Prepend an end-to-end series so the chart can show how the whole
  // pipeline trends, not just one service at a time. Amber stroke keeps
  // it visually distinct from the per-service phos/grey palette.
  const series = perf.wall_clock_series?.points?.length
    ? [
        {
          key: TOTAL_KEY,
          label: 'Total (end-to-end)',
          color: '#ffb35a',
          points: perf.wall_clock_series.points,
        },
        ...baseSeries,
      ]
    : baseSeries;

  // Auto-zoom the x-axis to the populated bucket range. When only the
  // latest bucket has data (e.g. a fresh test env on 24h), this keeps
  // the dots in the visible area instead of flush against the right
  // edge of an empty canvas.
  const xZoom = (() => {
    const lastIdx = (perf.bucket_labels?.length || 0) - 1;
    if (lastIdx <= 0) return null;
    const firstByseries = series
      .map((s) => (s.points || []).findIndex((p) => p != null))
      .filter((i) => i >= 0);
    if (!firstByseries.length) return null;
    const firstIdx = Math.min(...firstByseries);
    // Only zoom when more than half the range is empty; otherwise let
    // uPlot autoscale.
    if (firstIdx <= lastIdx * 0.5) return null;
    return { from: Math.max(0, firstIdx - 1), to: lastIdx };
  })();

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        {/* Left strip — Total pipeline pill + 5 service summary rows.
            Pills are multi-selectable; click toggles inclusion. Empty
            selection = no filter (chart shows all series). */}
        <div className={styles.strip}>
          <Mono className={styles.stripEyebrow}>Services · Avg pipeline time</Mono>
          {perf.wall_clock_avg_seconds != null && (
            <ServiceStripRow
              key={TOTAL_KEY}
              serviceKey={TOTAL_KEY}
              label="Total pipeline"
              description="End-to-end wall-clock"
              avgSeconds={perf.wall_clock_avg_seconds || 0}
              deltaPct={0}
              trend={sparkByService[TOTAL_KEY]}
              isUp={false}
              active={selectedServices.has(TOTAL_KEY)}
              onClick={() => toggleService(TOTAL_KEY)}
            />
          )}
          {SERVICE_ORDER.map((key) => {
            const s = services.find((x) => x.key === key) || {
              key,
              avg_seconds: 0,
              delta_pct: 0,
              is_regression: false,
            };
            return (
              <ServiceStripRow
                key={key}
                serviceKey={key}
                label={SERVICE_LABELS[key] || key}
                description={SERVICE_DESCRIPTIONS[key] || ''}
                avgSeconds={s.avg_seconds || 0}
                deltaPct={s.delta_pct || 0}
                trend={sparkByService[key]}
                isUp={Boolean(s.is_regression) || (s.delta_pct || 0) > 5}
                active={selectedServices.has(key)}
                onClick={() => toggleService(key)}
              />
            );
          })}
        </div>

        {/* Center — multi-line chart + regression callouts */}
        <div className={styles.chartCol}>
          <div className={styles.chartHead}>
            <Mono className={styles.stripEyebrow}>
              Avg time per service · last {range}
            </Mono>
            <span
              className={styles.chartTotal}
              title="services = Σ per-service averages (run in parallel). pipeline = end-to-end wall-clock per photo, averaged."
            >
              <Mono className={styles.chartTotalLabel}>services</Mono>{' '}
              {formatSeconds(perf.total_avg_seconds ?? 0)}
              {perf.wall_clock_avg_seconds != null && (
                <>
                  <span className={styles.chartTotalSep}>·</span>
                  <Mono className={styles.chartTotalLabel}>pipeline</Mono>{' '}
                  {formatSeconds(perf.wall_clock_avg_seconds)}
                </>
              )}
            </span>
          </div>
          <LineChart
            labels={perf.bucket_labels || []}
            series={series}
            selected={selectedServices}
            onSeriesClick={toggleService}
            yLabel={perf.unit || 'seconds'}
            height={260}
            xZoom={xZoom}
          />
          <div className={styles.regressions}>
            {(perf.regressions || []).map((r, i) => (
              <RegressionCallout
                key={`${r.service}-${i}`}
                service={r.service}
                fromSeconds={r.from_seconds}
                toSeconds={r.to_seconds}
                detectedAt={r.detected_at}
                likelyStep={r.likely_step}
                traceUrl={r.trace_url}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row — slow steps + service share */}
      <div className={styles.bottomRow}>
        <div className={styles.slowStepsCard}>
          <div className={styles.cardHead}>
            <Mono className={styles.stripEyebrow}>
              Top 8 slowest steps · all services
            </Mono>
            <Mono className={styles.cardHeadHint}>step · service · avg · trend</Mono>
          </div>
          <table className={styles.slowTable}>
            <thead>
              <tr>
                <th className={styles.slowRank}>#</th>
                <th>Step</th>
                <th>Service</th>
                <th className={styles.slowAvg}>Avg</th>
                <th className={styles.slowTrend}>Trend</th>
                <th className={styles.slowDelta}>Δ</th>
              </tr>
            </thead>
            <tbody>
              {(slowSteps?.steps || []).map((row, i) => (
                <tr key={`${row.service}-${row.name}-${i}`}>
                  <td className={styles.slowRank}>
                    {String(i + 1).padStart(2, '0')}
                  </td>
                  <td className={styles.slowName}>{row.name}</td>
                  <td className={styles.slowService}>
                    {SERVICE_LABELS[row.service] || row.service}
                  </td>
                  <td className={styles.slowAvg}>{formatMs(row.avg_ms)}</td>
                  <td className={styles.slowTrend}>
                    <Sparkline points={row.trend || []} isUp={Boolean(row.is_up)} />
                  </td>
                  <td className={styles.slowDelta}>
                    {(() => {
                      // direction is provided by the backend when
                      // available; falls back to is_up for older
                      // responses. "up" = regression (amber), "down" =
                      // improvement (phos), "flat" / missing = neutral.
                      const dir = row.direction || (row.is_up ? 'up' : 'flat');
                      if (dir === 'up') return <span className={styles.upArrow}>↑</span>;
                      if (dir === 'down') return <span className={styles.downArrow}>↓</span>;
                      return <span className={styles.flatArrow}>─</span>;
                    })()}
                  </td>
                </tr>
              ))}
              {(slowSteps?.steps || []).length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.slowEmpty}>
                    No step data available yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.shareCard}>
          <div className={styles.cardHead}>
            <Mono className={styles.stripEyebrow}>Service share · pipeline time</Mono>
            <span
              className={styles.cardHeadHint}
              title="services = Σ per-service averages (run in parallel). pipeline = end-to-end wall-clock per photo, averaged."
            >
              <Mono className={styles.chartTotalLabel}>services</Mono>{' '}
              {formatSeconds(perf.total_avg_seconds ?? 0)}
              {perf.wall_clock_avg_seconds != null && (
                <>
                  <span className={styles.chartTotalSep}>·</span>
                  <Mono className={styles.chartTotalLabel}>pipeline</Mono>{' '}
                  {formatSeconds(perf.wall_clock_avg_seconds)}
                </>
              )}
            </span>
          </div>
          <StackedBar
            segments={SERVICE_ORDER.map((key) => {
              const entry = (perf.share || []).find((x) => x.key === key);
              return {
                key,
                label: SERVICE_LABELS[key] || key,
                pct: entry?.share_pct || 0,
              };
            })}
            activeKey={
              // Pass an active segment only when exactly one service is
              // selected — multi-select is ambiguous for a stacked bar.
              selectedServices.size === 1 && !selectedServices.has(TOTAL_KEY)
                ? [...selectedServices][0]
                : null
            }
            height={10}
          />
          <ul className={styles.shareList}>
            {SERVICE_ORDER.map((key) => {
              const s = services.find((x) => x.key === key) || {};
              return (
                <li key={key} className={styles.shareItem}>
                  <span className={styles.shareDot} aria-hidden="true" />
                  <span className={styles.shareLabel}>
                    {SERVICE_LABELS[key] || key}
                  </span>
                  <span className={styles.shareSpacer} />
                  <Mono className={styles.shareValue}>
                    {formatSeconds(s.avg_seconds ?? 0)}
                  </Mono>
                  <Mono className={styles.sharePct}>
                    {(s.share_pct ?? 0).toFixed(1)}%
                  </Mono>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
